import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { Editor } from '@tiptap/core';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
type Props = {
  isOpen: boolean;
  editor: Editor | null;
  onClose(): void;
};

export const DescriptionEditorSetImageUrlModal: React.FC<Props> = ({ isOpen, editor, onClose }: Props) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [alt, setAlt] = useState<string>('');

  useEffect(() => {
    if (editor && isOpen) {
      const previousImageUrl = editor.getAttributes('image').src;
      const previousAlt = editor.getAttributes('image').alt;
      setImageUrl(previousImageUrl);
      setAlt(previousAlt);
    }
  }, [editor, isOpen]);

  const handleSubmit = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: imageUrl, alt }).run();
    setImageUrl('');
    setAlt('');
    onClose();
  }, [editor, onClose, imageUrl, alt]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('canvas.descriptionPanel.setImage.title')}
      submitButton={
        <Button color="primary" onClick={handleSubmit}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-4">
        <div className="pb-2 font-bold">{t('canvas.descriptionPanel.setImage.urlLabel')}</div>
        <Input
          type="text"
          placeholder={t('canvas.descriptionPanel.setImage.urlPlaceholder')}
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          autoFocus={true}
        />
      </div>
      <div className="p-4">
        <div className="pb-2 font-bold">{t('canvas.descriptionPanel.setImage.altLabel')}</div>
        <Input
          type="text"
          placeholder={t('canvas.descriptionPanel.setImage.altPlaceholder')}
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
        />
      </div>
    </GeneralModal>
  );
};
